/** The running version, centred in the footer: "LLM Opex v1.<commit>".
 *  GET /api/version says what runs and no more; the release history and this
 *  server's deploys are an administrator's (GET /api/admin/version). */
import { api } from '../api'
import { useAsync } from './useAsync'
import { useSite } from '../settings'

export interface VersionInfo {
  version: string; sha: string | null; date: string | null
  /** "dev" on a development server */
  env?: string; branch?: string | null; dirty?: boolean
}

let cached: Promise<VersionInfo> | null = null
/** One request per page load, shared by the footer and the DEV ribbon. */
export const loadVersion = () => (cached ??= api.version() as Promise<VersionInfo>)

export function VersionFooter() {
  const site = useSite()
  const v = useAsync<VersionInfo>(() => loadVersion(), [])
  if (!v.data) return null
  const dev = v.data.env === 'dev'
  return (
    <div className="version">
      {site.name} <b>v{v.data.version}</b>
      {dev ? <> · <b className="envtag">DEV</b>{v.data.branch ? <> · {v.data.branch}{v.data.dirty ? ' (uncommitted changes)' : ''}</> : null}</> : null}
    </div>
  )
}

/** Admin's header: which environment this server is (DEV or PROD, always
 *  shown) and exactly what it runs: version, commit and branch. */
export function EnvTag() {
  const v = useAsync<VersionInfo>(() => loadVersion(), [])
  if (!v.data) return null
  const dev = v.data.env === 'dev'
  if (dev && !document.title.startsWith('DEV · ')) document.title = `DEV · ${document.title}`
  return (
    <span className="envinfo" title={v.data.date ? `committed ${new Date(v.data.date).toLocaleString()}` : undefined}>
      <span className={dev ? 'envbadge dev' : 'envbadge prod'}>{dev ? 'DEV' : 'PROD'}</span>
      <span className="small">v{v.data.version}</span>
      <code className="small">{v.data.sha}</code>
      {v.data.branch ? <span className="small muted">{v.data.branch}{v.data.dirty ? ' + uncommitted changes' : ''}</span> : null}
    </span>
  )
}

/** A ribbon on a development server, so it is never mistaken for production. */
export function DevRibbon() {
  const v = useAsync<VersionInfo>(() => loadVersion(), [])
  if (v.data?.env !== 'dev') return null
  if (!document.title.startsWith('DEV · ')) document.title = `DEV · ${document.title}`
  return <span className="devribbon" title={v.data.branch ? `branch ${v.data.branch}` : undefined}>DEV</span>
}
